"use client";
import Helper from "@/utils/helper";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";

import CustomerConstants from "@/common/constants/customer";
import Loyalty from "@/common/constants/loyalty";
import ArrowLeftIcon from "@/components/icons/ArrowLeftIcon";
import InputLine from "@/components/ui/InputLine";

import { DatePicker } from "@/components/DatePicker";
import Button from "@/components/ui/Button";
import InputPassword from "@/components/ui/InputPassword";
import { checkSender, signUp } from "@/lib/api/account/auth";
import useTranslationApi from "@/lib/hooks/useTranslationApi";
import { setErrorsAuth, setPassword } from "@/redux/features/LoyaltySlice";
import { useAppDispatch } from "@/redux/hook";
import { SignupJson, SignupStatusJson } from "@/types/Auth.type";
import { Metadata } from "next";
import { toast } from "sonner";
import ToastMessage from "@/components/ui/ToastMessage";

interface IFormInput {
	last_name: string;
	first_name: string;
	phone: string;
	email: string;
	birthday?: number;
	password: string;
	password2: string;
}

type PropsHandle = {
	account_id: string;
	status: SignupStatusJson;
	type: number;
};
const RegisterPage = () => {
	const initialValues: IFormInput = {
		last_name: "",
		first_name: "",
		phone: "",
		email: "",
		birthday: 0,
		password: "",
		password2: "",
	};

	const { t } = useTranslationApi([
		"error_required",
		"is_establist_pw1",
		"is_establist_pw2",
		"is_establist_pw3",
		"is_establist_pw4",
		"register_heading",
		"create_password_1",
		"create_password_2",
		"create_password_3",
		"create_password_4",
		"create_password_5",
		"first_name_label",
		"last_name_label",
		"phone_label",
		"email_label",
		"password_label",
		"password2_label",
		"gender_label",
		"birthday_label",
		"customer_error_password_to_short",
		"error_password2_not_match",
		"customer_error_email_exists",
		"register_title_1",
		"register_title_2",
		"register_subtitle",
		"customer_ERROR_EMAIL_FORMAT",
		"customer_ERROR_PHONE_FORMAT",
		"error_phone_active",
		"customer_error_phone_exists",
	]);

	const {
		register,
		handleSubmit,
		setError,
		clearErrors,
		getValues,
		formState: { errors },
		watch,
		control,
	} = useForm<IFormInput>({
		reValidateMode: "onChange",
		mode: "onChange",
		defaultValues: initialValues,
	});
	const router = useRouter();
	const [cookies, setCookies] = useCookies(["account-id"]);
	const dispatch = useAppDispatch();

	const [status, setStatus] = useState<string>("");
	const [type, setType] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);

	// const [birthday, setBirthday] = useState<number>(0);

	const onErrors: SubmitErrorHandler<IFormInput> = (errors) => {};

	//////////////////////////
	const fetchFirstNavigate = async ({
		account_id,
		status,
		type,
	}: PropsHandle) => {
		const response = await checkSender(
			{
				account_id: account_id.trim(),
				type: type,
			},
			(err) => {
				const convertError = err?.errors || [];

				if (
					convertError.some((err: any) =>
						err.includes(Loyalty.ERRORS.ERROR_OTP_ALREADY)
					)
				) {
					router.push(
						`${
							CustomerConstants.LINK_ROUTER.LOGIN.VERIFY_OTP
						}?type=${JSON.stringify(
							type
						)}&account_id=${account_id}&status=${status}`
					);
				}
			}
		);

		if (response && response.data) {
			router.push(
				`${
					CustomerConstants.LINK_ROUTER.LOGIN.VERIFY_OTP
				}?type=${JSON.stringify(
					type
				)}&account_id=${account_id}&status=${status}`
			);
		}
	};

	const handleProcessSignup = (response: SignupJson, formData: IFormInput) => {
		switch (response.status) {
			case "CUSTOMER_ACTIVATED":
				// Khách cũ có tài khoản đã active , -> login
				setStatus("CUSTOMER_ACTIVATED");
				setError("phone", {
					message: t("error_phone_active"),
				});
				break;
			case "CUSTOMER_ALREADY": // khách cũ chưa có tài khoản , -> thiết lập mật khẩu
				setType(Loyalty.VERIFY.TYPE_CUSTOMER_CREATEPASSWORD);
				setStatus("CUSTOMER_ALREADY");
				break;
			case "CUSTOMER_UNACTIVATED": // Khách có tài khoản chưa active -> verify tài khoản
				setType(Loyalty.VERIFY.TYPE_CUSTOMER_ACTIVE);
				setStatus("CUSTOMER_UNACTIVATED");
				break;
			case "CUSTOMER_ALREADY_CREATED": // Khách cũ có tài khoản chưa active -> verify tài khoản
				setType(Loyalty.VERIFY.TYPE_CUSTOMER_ACTIVE);
				setStatus("CUSTOMER_ALREADY_CREATED");
				break;
			default:
				fetchFirstNavigate({
					account_id: formData.phone.trim(),
					status: "REGISTER_SUCCESS",
					type: Loyalty.VERIFY.TYPE_CUSTOMER_ACTIVE,
				});
				dispatch(setErrorsAuth({ errors: ["REGISTER_SUCCESS"] }));
				break;
		}
	};

	const handleEstablishPw = ({ account_id, status, type }: PropsHandle) => {
		fetchFirstNavigate({ account_id, status, type });
	};

	const handleActiveAccount = ({ account_id, status, type }: PropsHandle) => {
		fetchFirstNavigate({
			account_id: account_id,
			status: status,
			type: type,
		});
	};

	const isEstablishPw: boolean = useMemo(() => {
		const InEstablish = [
			Loyalty.STATUS_CUSTOMER.CUSTOMER_ALREADY_CREATED,
			Loyalty.STATUS_CUSTOMER.CUSTOMER_ALREADY,
		];
		if (InEstablish.includes(status)) {
			return true;
		}
		return false;
	}, [status]);

	const isActiveAcount: boolean = useMemo(() => {
		const InActiveAccount = [Loyalty.STATUS_CUSTOMER.CUSTOMER_UNACTIVATED];
		if (InActiveAccount.includes(status)) {
			return true;
		}
		return false;
	}, [status]);

	const onRegister: SubmitHandler<IFormInput> = async (formData) => {
		setLoading(true);
		dispatch(setPassword(formData.password2.trim()));

		const response = await signUp(
			{
				phone: formData.phone.trim(),
				password: formData.password.trim(),
				password2: formData.password2.trim(),
				birthday: formData.birthday ? formData.birthday / 1000 : undefined,
				first_name: formData.first_name.trim()
					? formData.first_name.trim()
					: "",
				last_name: formData.last_name.trim() ? formData.last_name.trim() : "",
				email: formData.email.trim() ? formData.email.trim() : "",
			},
			(err) => {
				const convertError = err?.errors || [];
				if (convertError.includes(Loyalty.ERRORS.ERROR_EMAIL_EXISTS)) {
					setError("email", {
						message: t("customer_error_email_exists"),
					});
				} else if (convertError.includes(Loyalty.ERRORS.ERROR_PHONE_EXISTS)) {
					setError("phone", {
						message: t("customer_error_phone_exists"),
					});
				} else {
					toast(
						<ToastMessage type="error">
							<p>Đăng ký thất bại! Vui lòng thử lại sau</p>
						</ToastMessage>
					);
				}

				// else if (convertError.includes(Loyalty.ERRORS.ERROR_PHONE_EXISTS)) {
				// 	setError("email", {
				// 		message: t("customer_error_phone_exists"),
				// 	});
				// }
			}
		);

		if (response.data) {
			if (response.data.hasOwnProperty("status")) {
				handleProcessSignup(
					{
						status: response.data.status,
					},
					formData
				);
			} else {
				handleProcessSignup(
					{
						status: "REGISTER_SUCCESS",
					},
					formData
				);
			}
		}

		setLoading(false);
	};

	return (
		<div className="w-full my-[24px] mx-auto py-0 px-4 sm:w-[392px] sm:max-x-[392px] sm:p-0 md:my-[40px] md:mx-auto">
			<div className="flex justify-center items-center gap-1">
				<span
					className="cursor-pointer"
					onClick={() => {
						router.back();
					}}>
					<ArrowLeftIcon />
				</span>
				<h4 className="text-center text-h4 font-medium">
					{t("register_heading")}
				</h4>
			</div>
			<div className="mt-4 text-center text-sm">
				{status.length > 0 ? (
					isEstablishPw ? (
						<>
							<p className="text-red-500 text-sm">{t("is_establist_pw1")}</p>
							<p className="text-red-500 text-sm">
								<span
									className="text-blue-500 cursor-pointer underline"
									onClick={() =>
										handleEstablishPw({
											account_id: getValues("phone") || "0",
											status: "CUSTOMER_ALREADY",
											type: Loyalty.VERIFY.TYPE_CUSTOMER_CREATEPASSWORD,
										})
									}>
									{t("is_establist_pw2")}
								</span>{" "}
								{t("is_establist_pw3")}
							</p>
							<p className="text-red-500 text-sm">{t("is_establist_pw4")}</p>
						</>
					) : isActiveAcount ? (
						<>
							<p className="text-red-500 text-sm">{t("create_password_1")}</p>
							<p className="text-red-500 text-sm">
								{t("create_password_2")}{" "}
								<span
									className="text-[#004b8f] cursor-pointer underline"
									onClick={() =>
										handleActiveAccount({
											account_id: getValues("phone") || "0",
											status: "CUSTOMER_UNACTIVATED",
											type: Loyalty.VERIFY.TYPE_CUSTOMER_ACTIVE,
										})
									}>
									{t("create_password_3")}
								</span>{" "}
								{t("create_password_4")}
							</p>
							<p className="text-red-500 text-sm">{t("create_password_5")}</p>
						</>
					) : status === Loyalty.STATUS_CUSTOMER.CUSTOMER_ACTIVATED ? (
						<>
							<p>{t("register_title_1")}</p>
							<p>{t("register_title_2")}</p>
						</>
					) : (
						""
					)
				) : (
					<>
						<p>{t("register_title_1")}</p>
						<p>{t("register_title_2")}</p>
					</>
				)}
			</div>
			<div className="flex justify-center items-center"></div>
			<form onSubmit={handleSubmit(onRegister, onErrors)} className="mt-[40px]">
				<div className="grid grid-cols-2 gap-3">
					<div className="">
						<InputLine
							name="last_name"
							control={control}
							rules={{ required: t("error_required") }}
							label={t("last_name_label") + "*"}
							showError={false}
						/>
						{errors.last_name && (
							<p className="text-red-500 text-sm mt-2">
								{errors.last_name.message}
							</p>
						)}
					</div>
					<div className="">
						<InputLine
							name="first_name"
							control={control}
							rules={{ required: t("error_required") }}
							label={t("first_name_label") + "*"}
							showError={false}
						/>
						{errors.first_name && (
							<p className="text-red-500 text-sm mt-2">
								{errors.first_name.message}
							</p>
						)}
					</div>
				</div>
				<div className="grid grid-cols-1">
					<div className="">
						<InputLine
							name="phone"
							inputClassName="w-full"
							control={control}
							rules={{
								required: t("error_required"),
								validate: (value) => {
									if (
										value &&
										value !== "" &&
										!Helper.validatePhoneNumber(value)
									) {
										return t("customer_ERROR_PHONE_FORMAT");
									} else {
										return true;
									}
								},
							}}
							label={t("phone_label") + "*"}
							showError={false}
						/>
						{errors.phone && (
							<p className="text-red-500 text-sm mt-2">
								{errors.phone.message}
							</p>
						)}
					</div>
					<div className="">
						<InputLine
							name="email"
							inputClassName="w-full"
							control={control}
							defaultValue=""
							rules={{
								validate: (value: string) => {
									if (value && value.trim().length > 0) {
										if (Helper.validateEmail(value)) {
											return true; // Email hợp lệ
										} else {
											return t("customer_ERROR_EMAIL_FORMAT");
										}
									} else {
										return true;
									}
								},
							}}
							label="Email"
							showError={false}
						/>
						{errors.email && (
							<p className="text-red-500 text-sm mt-2">
								{errors.email.message}
							</p>
						)}
					</div>
					<div className="">
						<InputPassword
							name="password"
							inputClassName="w-full"
							control={control}
							rules={{
								required: t("error_required"),
								minLength: {
									value: 8,
									message: t("customer_error_password_to_short"),
								},
								maxLength: {
									value: 15,
									message: t("customer_error_password_to_short"),
								},
								// pattern: {
								// 	value: /^[a-zA-Z0-9]+$/,
								// 	message: t("customer_error_password_to_short"),
								// },
							}}
							label={t("password_label") + "*"}
							showError={false}
						/>
						{errors.password && (
							<p className="text-red-500 text-sm mt-2">
								{errors.password.message}
							</p>
						)}
					</div>
					<div className="">
						<InputPassword
							inputClassName="w-full"
							name="password2"
							control={control}
							rules={{
								required: t("error_required"),
								validate: (value) => {
									if (!value || getValues("password") === value) {
										return true;
									} else {
										return t("error_password2_not_match");
									}
								},
							}}
							label={t("password2_label") + "*"}
							showError={false}
						/>
						{errors.password2 && (
							<p className="text-red-500 text-sm mt-2">
								{errors.password2.message}
							</p>
						)}
					</div>

					<div className="mt-3">
						<DatePicker
							placeholder={t("birthday_label")}
							name="birthday"
							control={control}
							setError={setError}
							clearErrors={clearErrors}
						/>
						{errors.birthday && (
							<p className="text-red-500 text-sm mt-2">
								{errors.birthday.message}
							</p>
						)}
					</div>
				</div>
				<Button
					disabled={loading}
					loading={loading}
					label={<span>{t("register_heading")}</span>}
					mode="dark"
					type="submit"
					className="w-full mt-4"
				/>
			</form>
			<p className="text-sm mt-4">
				{t("register_subtitle")} <span className="font-bold">namperfume.</span>
			</p>
		</div>
	);
};

export default RegisterPage;
